import React, { Component } from 'react';
import { Box, Typography, CircularProgress, TextField } from '@mui/material';
import PromotionPrerequisiteAckModal from '../PromotionPrerequisiteAckModal';
import { structuredApiErrorMessage } from '../investorPromotionPrerequisites';
import { StyledButton } from '../../styles/StyledButtons';
import { StyledHeading } from '../../styles/styles';
import AssignFundOpportunityModal from '../AssignFundOpportunityModal';
import { ContactFundOpportunityKanbanInvestmentPromptModal } from './ContactFundOpportunityKanbanInvestmentPromptModal';
import { ContactFundOpportunityKanbanStageColumn } from './ContactFundOpportunityKanbanStageColumn';
import { filterOpportunitiesForStage } from './contactFundOpportunityKanbanFilters';
import { loadContactFundOpportunities } from './loadContactFundOpportunities';
import { runInvestorOperationWithPrerequisites } from './contactFundOpportunityKanbanPrerequisites';
import {
  deleteOpportunity as deletePipelineOpportunity,
  demoteOpportunity as demotePipelineOpportunity,
  promoteOpportunity as promotePipelineOpportunity,
} from './contactFundOpportunityKanbanStageActions';
import {
  ContactFundOpportunityKanbanProps,
  ContactFundOpportunityKanbanState,
  FundOpportunityWithInvestor,
} from './contactFundOpportunityKanbanTypes';

export type { ContactFundOpportunityKanbanProps } from './contactFundOpportunityKanbanTypes';

export class ContactFundOpportunityKanbanImpl extends Component<
  ContactFundOpportunityKanbanProps,
  ContactFundOpportunityKanbanState
> {
  constructor(props: ContactFundOpportunityKanbanProps) {
    super(props);
    this.state = {
      fundOpportunities: [],
      loading: false,
      loaded: false,
      opportunityFilter: '',
    };
  }

  componentDidMount() {
    this.loadFundOpportunities();
  }

  componentDidUpdate(prevProps: ContactFundOpportunityKanbanProps) {
    if (this.props.contactId !== prevProps.contactId) {
      this.setState({ fundOpportunities: [], loaded: false }, () => this.loadFundOpportunities());
    }
  }

  closePromotionPrerequisiteModal = () => {
    this.setState({
      promotionPrerequisiteModalOpen: false,
      promotionPrerequisiteIssues: [],
      promotionPrerequisiteRetry: undefined,
      promotionPrerequisiteOnSuccess: undefined,
      promotionPrerequisiteConfirmInProgress: false,
    });
  };

  confirmPromotionPrerequisiteModal = () => {
    const retry = this.state.promotionPrerequisiteRetry;
    if (!retry) {
      return;
    }
    const onSuccessCb = this.state.promotionPrerequisiteOnSuccess;

    this.setState({ promotionPrerequisiteConfirmInProgress: true });
    retry()
      .then((response: unknown) => {
        if (
          typeof response === 'object' &&
          response !== null &&
          (response as { adminWarning?: { requiresConfirmation?: boolean; message?: string } })
            .adminWarning?.requiresConfirmation
        ) {
          const warning = (response as { adminWarning: { message?: string } }).adminWarning;
          this.props.onWarning?.(
            `${warning.message ?? 'Action requires confirmation'}. Consider using Fund Opportunity Admin for detailed steps.`,
          );
          this.closePromotionPrerequisiteModal();
          return;
        }
        this.closePromotionPrerequisiteModal();
        onSuccessCb?.();
      })
      .catch((err: unknown) => {
        console.error('Promotion prerequisite retry failed:', err);
        this.setState({ promotionPrerequisiteConfirmInProgress: false });
        this.props.onError(structuredApiErrorMessage(err));
      });
  };

  getStageActionDeps = () => ({
    onError: this.props.onError,
    onReloadContact: this.props.onReloadContact,
    reloadOpportunities: this.loadFundOpportunities,
    handleWithPrerequisites: this.handleInvestorOperationWithPrerequisites,
    collectInvestment: this.collectInvestment,
  });

  handleInvestorOperationWithPrerequisites = (
    run: (options: { overrideFundStatusRestriction?: boolean }) => Promise<void>,
    successCallback: () => void,
    operationName: string,
  ) => {
    runInvestorOperationWithPrerequisites(run, successCallback, operationName, {
      onError: this.props.onError,
      onWarning: this.props.onWarning,
      openPrerequisiteModal: ({ issues, retry, onSuccess }) => {
        this.setState({
          promotionPrerequisiteModalOpen: true,
          promotionPrerequisiteIssues: issues,
          promotionPrerequisiteRetry: retry,
          promotionPrerequisiteOnSuccess: onSuccess,
        });
      },
    });
  };

  collectInvestment = (label: string, currentInvestmentAmount?: number): Promise<number> => {
    return new Promise((resolve) => {
      this.setState({
        investmentPromptOpen: true,
        investmentPromptLabel: label,
        investmentPromptValue: currentInvestmentAmount ? String(currentInvestmentAmount) : '',
        investmentPromptResolver: resolve,
      });
    });
  };

  loadFundOpportunities = async () => {
    if (!this.props.contactId || this.state.loading) {
      return;
    }

    this.setState({ loading: true, error: undefined });

    try {
      const filteredOpportunities = await loadContactFundOpportunities(this.props.contactId);
      this.setState({
        fundOpportunities: filteredOpportunities,
        loading: false,
        loaded: true,
      });
    } catch (error: unknown) {
      console.error('Failed to load fund opportunities:', error);
      const message = error instanceof Error ? error.message : 'Failed to load fund opportunities';
      this.setState({
        loading: false,
        loaded: true,
        error: message,
      });
      this.props.onError(message);
    }
  };

  handleRefresh = () => {
    this.setState({ loaded: false }, () => this.loadFundOpportunities());
  };

  getOpportunitiesForStage = (stage: string) => {
    return filterOpportunitiesForStage(
      this.state.fundOpportunities,
      stage,
      this.state.opportunityFilter,
    );
  };

  promoteOpportunity = (opportunity: FundOpportunityWithInvestor) => {
    promotePipelineOpportunity(opportunity, this.getStageActionDeps());
  };

  demoteOpportunity = (opportunity: FundOpportunityWithInvestor) => {
    demotePipelineOpportunity(opportunity, this.getStageActionDeps());
  };

  deleteOpportunity = (opportunity: FundOpportunityWithInvestor) => {
    deletePipelineOpportunity(opportunity, this.getStageActionDeps());
  };

  closeInvestmentPrompt = () => {
    this.setState({
      investmentPromptOpen: false,
      investmentPromptResolver: undefined,
      investmentPromptValue: undefined,
      investmentPromptError: undefined,
    });
  };

  renderStageColumn = (
    title: string,
    stage: string,
    options: { showPromote?: boolean; showDemote?: boolean; showDelete?: boolean },
  ) => (
    <ContactFundOpportunityKanbanStageColumn
      key={stage}
      title={title}
      stage={stage}
      loaded={this.state.loaded}
      opportunities={this.getOpportunitiesForStage(stage)}
      showPromote={options.showPromote}
      showDemote={options.showDemote}
      showDelete={options.showDelete}
      onPromote={this.promoteOpportunity}
      onDemote={this.demoteOpportunity}
      onDelete={this.deleteOpportunity}
    />
  );

  render() {
    const { fundOpportunities, loading, loaded, error, opportunityFilter } = this.state;
    const { contact } = this.props;
    const custodian = contact?.custodian;
    const pipelineBusy = loading && loaded && fundOpportunities.length > 0;

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <StyledHeading tone="tone1">Fund Opportunities Pipeline</StyledHeading>
          {!custodian && (
            <StyledButton
              variant="outlined"
              size="small"
              onClick={() => this.setState({ assignOpportunityModalOpen: true })}
            >
              Assign Opportunity
            </StyledButton>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Filter by name or status..."
            value={opportunityFilter}
            onChange={(event) => this.setState({ opportunityFilter: event.target.value })}
            size="small"
            fullWidth
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
          />
        </Box>

        {error && !loading && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        <Box sx={{ position: 'relative' }}>
          {pipelineBusy && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10,
                borderRadius: '8px',
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}

          {loaded && fundOpportunities.length === 0 && !loading ? (
            <Typography color="text.secondary">No fund opportunities found.</Typography>
          ) : (
            <Box
              className="pipeline-board"
              display="flex"
              gap={2}
              sx={{ mt: 2, overflow: 'hidden' }}
            >
              {this.renderStageColumn('Candidate', 'CANDIDATE', {
                showPromote: true,
                showDelete: true,
              })}
              {this.renderStageColumn('Lead', 'LEAD', {
                showPromote: true,
                showDemote: true,
              })}
              {this.renderStageColumn('Lead with EOI', 'LEAD_WITH_EOI', {
                showPromote: true,
                showDemote: true,
              })}
              {this.renderStageColumn('Opportunity', 'OPPORTUNITY', {
                showPromote: true,
                showDemote: true,
              })}
              {this.renderStageColumn('Committed Investor', 'COMMITTED_INVESTOR', {
                showDemote: true,
              })}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <StyledButton
            variant="outlined"
            size="small"
            onClick={this.handleRefresh}
            disabled={loading}
          >
            Refresh
          </StyledButton>
        </Box>

        {this.state.investmentPromptOpen && (
          <ContactFundOpportunityKanbanInvestmentPromptModal
            open
            title={this.state.investmentPromptLabel}
            value={this.state.investmentPromptValue}
            error={this.state.investmentPromptError}
            onClose={this.closeInvestmentPrompt}
            onValueChange={(value) =>
              this.setState({
                investmentPromptValue: value,
                investmentPromptError: undefined,
              })
            }
            onSubmit={() => {
              const raw = this.state.investmentPromptValue;
              const value = raw != null && raw !== '' ? Number(raw) : NaN;
              if (Number.isNaN(value)) {
                this.setState({ investmentPromptError: 'Enter a valid amount' });
                return;
              }
              this.state.investmentPromptResolver?.(value);
              this.closeInvestmentPrompt();
            }}
          />
        )}

        <PromotionPrerequisiteAckModal
          open={!!this.state.promotionPrerequisiteModalOpen}
          issues={this.state.promotionPrerequisiteIssues ?? []}
          inProgress={this.state.promotionPrerequisiteConfirmInProgress}
          onCancel={this.closePromotionPrerequisiteModal}
          onConfirm={this.confirmPromotionPrerequisiteModal}
        />

        <AssignFundOpportunityModal
          contact={
            { id: this.props.contactId } as unknown as import('../../../models/Contact').default
          }
          isOpen={!!this.state.assignOpportunityModalOpen}
          toggle={() => this.setState({ assignOpportunityModalOpen: false })}
          onComplete={() => {
            this.loadFundOpportunities();
            this.setState({ assignOpportunityModalOpen: false });
          }}
        />
      </Box>
    );
  }
}

export default ContactFundOpportunityKanbanImpl;
